(() => {
  const tag = 'home-influencer-endorsements-carousel';
  if (customElements.get(tag)) return;

  customElements.define(tag, class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-influencer-track]');
      this.cards = [...this.querySelectorAll('.home-influencer-endorsements__card')];
      if (!this.track || !this.cards.length) return;

      this.controller = new AbortController();
      const { signal } = this.controller;
      this.previousButton = this.querySelector('[data-influencer-direction="previous"]');
      this.nextButton = this.querySelector('[data-influencer-direction="next"]');
      this.status = this.querySelector('[data-influencer-status]');
      this.mediaQuery = matchMedia('(min-width: 750px)');

      this.previousButton?.addEventListener('click', () => this.move(-1), { signal });
      this.nextButton?.addEventListener('click', () => this.move(1), { signal });
      this.track.addEventListener('scroll', () => this.scheduleUpdate(), { signal, passive: true });
      this.track.addEventListener('pointerdown', (event) => this.startDrag(event), { signal });
      this.track.addEventListener('pointermove', (event) => this.drag(event), { signal });
      this.track.addEventListener('pointerup', (event) => this.finishDrag(event), { signal });
      this.track.addEventListener('pointercancel', (event) => this.finishDrag(event), { signal });
      this.track.addEventListener('click', (event) => {
        if (event.target.closest?.('a, button, input, select, textarea')) return;
        if (this.mediaQuery.matches && !this.dragMoved) this.move(1);
      }, { signal });
      this.mediaQuery.addEventListener('change', () => this.update(), { signal });
      document.addEventListener('shopify:block:select', (event) => this.selectEditorBlock(event), { signal });

      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
      this.update();
    }

    get index() {
      if (!this.track.clientWidth) return 0;
      return Math.max(0, Math.min(this.cards.length - 1, Math.round(this.track.scrollLeft / this.track.clientWidth)));
    }

    goTo(index, behavior = 'smooth') {
      if (!this.mediaQuery.matches) return;
      const target = Math.max(0, Math.min(this.cards.length - 1, index));
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior });
      this.scheduleUpdate();
    }

    move(direction) {
      this.goTo(this.index + direction, matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth');
    }

    startDrag(event) {
      if (!this.mediaQuery.matches || event.pointerType !== 'mouse' || event.button !== 0) return;
      this.dragPointer = event.pointerId;
      this.dragStart = event.clientX;
      this.dragScroll = this.track.scrollLeft;
      this.dragMoved = false;
      this.track.setPointerCapture(event.pointerId);
      this.setAttribute('dragging', '');
    }

    drag(event) {
      if (event.pointerId !== this.dragPointer) return;
      const delta = event.clientX - this.dragStart;
      if (Math.abs(delta) > 4) this.dragMoved = true;
      this.track.scrollLeft = this.dragScroll - delta;
      if (this.dragMoved) event.preventDefault();
    }

    finishDrag(event) {
      if (event.pointerId !== this.dragPointer) return;
      const delta = event.clientX - this.dragStart;
      const startIndex = Math.round(this.dragScroll / this.track.clientWidth);
      const target = Math.abs(delta) > this.track.clientWidth * .12
        ? startIndex + (delta < 0 ? 1 : -1)
        : this.index;
      this.track.releasePointerCapture?.(event.pointerId);
      this.dragPointer = null;
      this.removeAttribute('dragging');
      this.goTo(target, matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth');
      setTimeout(() => { this.dragMoved = false; });
    }

    selectEditorBlock(event) {
      const card = event.target?.closest?.('.home-influencer-endorsements__card');
      if (!card || !this.contains(card)) return;
      this.goTo(this.cards.indexOf(card), 'instant');
    }

    scheduleUpdate() {
      cancelAnimationFrame(this.updateFrame);
      this.updateFrame = requestAnimationFrame(() => this.update());
    }

    update() {
      const desktop = this.mediaQuery.matches;
      const index = desktop ? this.index : 0;
      this.cards.forEach((card, cardIndex) => {
        card.toggleAttribute('inert', desktop && cardIndex !== index);
        if (desktop) card.setAttribute('aria-hidden', cardIndex === index ? 'false' : 'true');
        else card.removeAttribute('aria-hidden');
      });
      if (this.previousButton) this.previousButton.disabled = !desktop || index === 0;
      if (this.nextButton) this.nextButton.disabled = !desktop || index === this.cards.length - 1;
      if (this.status) this.status.textContent = desktop ? `Endorsement ${index + 1} of ${this.cards.length}` : '';
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.updateFrame);
      this.controller = null;
    }
  });
})();
